import React, { ReactNode } from "react";

import Accordion from "react-bootstrap/Accordion";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import Banner from "./banner";
import useMediaQuery from "../../hooks/haiku/useMediaQuery";
import KontentItemImage from "../../templates/kontentItemImage";
import NavigationLink from "../navigationLink/navigationLink";
import StateAwareAccordionToggle from "../stateAwareAccordionToggle/stateAwareAccordionToggle";

import "./faqBanner.scss";

export interface FaqItem {
  id: string;
  question: string;
  answer: ReactNode;
  anchorSlug: string;
}

interface FaqBannerProps {
  title: string;
  buttonText: string;
  buttonUrl: string;
  imageData?: Queries.kontent_item_image;
  faqItems: FaqItem[];
  maxItems?: number;
  headlineAs?: "h2" | "div";
}

const FaqBanner: React.FC<FaqBannerProps> = ({
  title,
  buttonText,
  buttonUrl,
  imageData = undefined,
  faqItems,
  maxItems = 6,
  headlineAs: HeadlineComponent = "h2",
}) => {
  const isHigherMd = useMediaQuery("(min-width: 768px)", false);
  const isLowerXl = useMediaQuery("(max-width: 1199px)", true);

  const filteredFaqItems = faqItems.slice(0, isLowerXl ? 3 : maxItems).filter((faqItem) => faqItem);

  let image = <KontentItemImage data={imageData} width={800} imageOnlyWidth aspectRatio={16 / 9} />;

  if (isHigherMd) {
    image = <KontentItemImage data={imageData} width={600} aspectRatio={3 / 4} />;
  }

  let defaultActiveKeyObject: { defaultActiveKey?: string } = {};

  if (typeof window !== "undefined") {
    const { hash } = window.location;

    if (hash !== "") {
      const accordionItem = filteredFaqItems.find((item) => `#${item.anchorSlug}` === hash);

      if (accordionItem) {
        defaultActiveKeyObject = { defaultActiveKey: accordionItem.id };
      }
    }
  }

  return (
    <Banner className="faq-banner" mediaObject={image}>
      <Row className="faq-banner__inner">
        <Col md={7}>
          <HeadlineComponent className={HeadlineComponent !== "h2" ? "typo-h2" : undefined}>{title}</HeadlineComponent>
          <Accordion className="faq-banner__items" {...defaultActiveKeyObject}>
            {filteredFaqItems.map((item, i) => (
              <div
                className="faq-banner__item"
                key={item.question}
                {...(item.anchorSlug ? { id: item.anchorSlug } : {})}
              >
                <StateAwareAccordionToggle eventKey={i.toString()}>
                  <div className="typo-h4 d-flex justify-content-between align-items-center">
                    {item.question}
                  </div>
                </StateAwareAccordionToggle>

                <Accordion.Collapse className="faq-banner__item-answer" eventKey={i.toString()}>
                  <>
                    {item.answer}
                  </>
                </Accordion.Collapse>

                <hr className="faq-banner__seperator" />
              </div>
            ))}
          </Accordion>
          <NavigationLink href={buttonUrl} className="btn btn-plain-faq">
            {buttonText}
          </NavigationLink>
        </Col>
      </Row>
    </Banner>
  );
};

export default FaqBanner;
